<script lang="ts" setup>
import { computed, onMounted, ref, watch } from 'vue'
import { useInfiniteScroll, useTitle } from '@vueuse/core'
import { useRoute, useRouter } from 'vue-router/composables'
import { commonTexts } from './configs/common'
import CollectionPageDefault from './components/CollectionPage.default.vue'
import CollectionPageFlipnovel from './components/CollectionPage.flipnovel.vue'
import { api } from '@/api'
import { brand } from '@/config/brands'
import { useFinishedClick, useFinishedView } from '@/composables/tracks'
import { loadBrandComponent } from '@/utils/loadBrandConfig'
import { READER_ROUTE_NAME } from '@/utils/readerRouteName'

const props = withDefaults(
  defineProps<{
    columnId: string
    type?: 'recommend' | 'ordinary' | 'finish'
  }>(),
  {
    type: 'ordinary',
  },
)

const CollectionPage = loadBrandComponent({
  rocnovel: CollectionPageDefault,
  oceread: CollectionPageDefault,
  talewave: CollectionPageDefault,
  ficmoon: CollectionPageDefault,
  novelarc: CollectionPageDefault,
  storyella: CollectionPageDefault,
  silktale: CollectionPageDefault,
  pearla: CollectionPageDefault,
  fictora: CollectionPageDefault,
  marinovel: CollectionPageDefault,
  flipnovel: CollectionPageFlipnovel,
  moonpage: CollectionPageFlipnovel,
  storlyn: CollectionPageFlipnovel,
  novaris: CollectionPageFlipnovel,
})

const route = useRoute()
const router = useRouter()
const isFlipnovelFamily = ['flipnovel', 'storlyn', 'moonpage', 'novaris'].includes(brand)
const { finishedView } = useFinishedView()
const { finishedClick } = useFinishedClick()
const allData = ref<any>({})
const title = useTitle('column')
const collections = ref<any>([])
const page = ref(1)
const fetching = ref(false)
const ending = ref(false)
const error = ref<any>()
const pagination = computed(() => ({
  pageIndex: page.value,
  pageSize: 20,
}))

function resetState() {
  collections.value = []
  page.value = 1
  ending.value = false
  fetching.value = false
  error.value = undefined
}

const getCollectionByType = async function (type = 'ordinary') {
  let result: any = []
  if (type === 'recommend')
    result = await api.get<any, GetContentGetcolumncontentlistResponse>('/content/getLikeColumnContentList', { params: { columnId: props.columnId, ...pagination.value } })
  else if (type === 'finish')
    result = await api.get<any, GetContentGetfinishlikecolumnResponse>('/content/getFinishLikeColumn', { params: { contentId: props.columnId, pageIndex: 1, pageSize: 30 } })
  else
    result = await api.get<any, GetContentGetcolumncontentlistResponse>('/content/getColumnContentList', { params: { columnId: props.columnId, ...pagination.value } })

  return result.data
}

async function fetchCollection() {
  if (fetching.value || ending.value)
    return
  fetching.value = true
  try {
    const data = await getCollectionByType(route.query.type as string)

    // 对于 finish 类型，最多只展示30条数据
    if (route.query.type === 'finish') {
      collections.value = data.resourceList.records || []
      ending.value = true

      // 完读推荐列表页曝光埋点
      finishedView({
        contentId: props.columnId,
        serialStatus: route.query.status === 'finish' ? 'completed' : 'ongoing',
        showNum: collections.value.length,
        title: 'finished_reclist',
      })
    }
    else {
      // 其他类型保持原有的分页逻辑
      collections.value = [...collections.value, ...data.resourceList.records]
      if (page.value >= data.resourceList.pages)
        ending.value = true
      else
        page.value += 1
    }

    title.value = data.showName || 'column'

    // 同步栏目标题到路由 query，供 flipnovel 公共头部读取
    if (isFlipnovelFamily && data.showName && route.query.title !== data.showName) {
      router.replace({
        query: { ...route.query, title: data.showName },
      })
    }
    allData.value = data
  }
  catch (_error) {
    error.value = _error
  }
  finally {
    fetching.value = false
  }
}
function handleBookClick(bookId: string, firstChapterId?: string) {
  // 仅在完读推荐列表页触发点击埋点
  if (route.query.type === 'finish') {
    finishedClick({
      elementName: 'bookclick',
      contentId: props.columnId,
      serialStatus: route.query.status === 'finish' ? 'completed' : 'ongoing',
      showNum: collections.value.length,
      title: 'finished_reclist',
      contentIdRecbook: bookId,
    })
  }

  router.push({
    name: READER_ROUTE_NAME,
    params: { contentId: bookId, chapterId: firstChapterId || '' },
  })
}

onMounted(() => {
  fetchCollection()
})

watch(() => [props.columnId, route.query.type], () => {
  resetState()
  fetchCollection()
})

const infiniteRef = ref<HTMLElement>()
function setInfiniteRef(el: HTMLElement | null) {
  infiniteRef.value = el || undefined
}
useInfiniteScroll(infiniteRef,
  async () => {
    if (fetching.value || ending.value || error.value)
      return

    await fetchCollection()
  },
  { distance: 50 },
)
</script>

<template>
  <div ref="infiniteRef" class="h-100dvh overflow-scroll" :class="isTalewave ? 'talewave-collection-root' : 'bg-n-bg-global'">
    <BasicNavbar class="color-n-text-2" />

    <div class="w-full h-40px flex items-center justify-center fixed top-0 z-9 bg-n-bg-global font-medium text-n-text-1">
      {{ title }}
    </div>

    <div class="mt-50px px-5">
      <div v-for="(item, index) in collections" :key="index">
        <div
          class="mb-15px flex items-center space-x-15px cursor-pointer"
          @click="handleBookClick(item.id, item.firstChapter?.id)"
        >
          <BizBookCover
            :id="uuid()"
            :src="item.contentCoverUrl || ''"
            :extra="{
              module_name: allData.showName,
              module_id: allData.id,
              module_type: allData.columnType,
              author: item.authorInfo.author,
              ...item,
            }"
            class="w-18 rounded"
          />

          <div class="flex-1 overflow-hidden">
            <p class="text-15px font-bold line-clamp-1 text-n-text-1">
              {{ item.contentName }}
            </p>
            <p class="text-13px mt-10px text-n-text-2 line-clamp-2">
              {{ item.introduce }}
            </p>

            <BizShowTags v-if="item.tagList && item.tagList.length" :tags="item.tagList" class="mt-10px" />
          </div>
        </div>
      </div>
    </div>

    <div v-if="ending" class="py-15px text-13px text-center text-n-text-3">
      {{ commonTexts.noMore }}
    </div>
    <div v-else-if="fetching" class="py-15px flex items-center justify-center space-x-4 text-13px text-n-text-3">
      <div class="animate-spin">
        <i-icon-park-outline-loading />
      </div>
      <p>{{ commonTexts.loading }}</p>
    </div>
  </div>
</template>
